import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import Spinner from 'components/Spinner';
import { FormCheckbox } from 'view/components/Form';
import {
	fetchPortfolioSetSettingsByPropertyId,
	setNotification,
} from 'reducers/portfolioSettingsReducer';
import './PropertyNotificationsSection.css';

const PropertyNotificationsSection = memo(
	({ portfolioSetId, propertyId, propertyAddress }) => {
		const dispatch = useDispatch();
		const [isInitialized, setIsInitialized] = useState(false);

		const { isLoading, portfolioSettings, userId } = useSelector((state) => ({
			isLoading:
				state.portfolioSettings.isLoading ||
				state.portfolioSettings.isRemovingProperty,
			portfolioSettings: state.portfolioSettings.data || {},
			userId: state.user.userData?.id,
		}));

		// Initialize form with default values
		const { control, reset, watch, setValue } = useForm({
			defaultValues: {},
		});

		// Fetch portfolio settings when component mounts or propertyId changes
		useEffect(() => {
			if (propertyId && userId && !isInitialized) {
				dispatch(
					fetchPortfolioSetSettingsByPropertyId(portfolioSetId, propertyId)
				)
					.then(() => setIsInitialized(true))
					.catch((error) => {
						console.error('Error fetching portfolio settings:', error);
						setIsInitialized(true);
					});
			}
			// eslint-disable-next-line
		}, [propertyId, userId, isInitialized]);

		// Reset form values when portfolio settings change
		useEffect(() => {
			if (
				portfolioSettings.notificationTypes &&
				portfolioSettings.notificationTypes.length > 0
			) {
				const formValues = {};
				let allEnabled = true;
				portfolioSettings.notificationTypes.forEach((notificationType) => {
					const isEnabled = notificationType.enabled === 1;
					formValues[`notification_${notificationType.id}`] = isEnabled;
					if (!isEnabled) {
						allEnabled = false;
					}
				});
				formValues.selectAll = allEnabled;
				reset(formValues);
			}
		}, [portfolioSettings, reset]);

		const handleCheckboxChange = (notificationId, isEnabled) => {
			if (portfolioSettings.id) {
				dispatch(
					setNotification(portfolioSettings.id, notificationId, isEnabled)
				);

				// Update Select All checkbox based on all notification states
				setTimeout(() => {
					const currentValues = watch();
					const allChecked = portfolioSettings.notificationTypes.every(
						(notificationType) =>
							currentValues[`notification_${notificationType.id}`]
					);
					setValue('selectAll', allChecked);
				}, 0);
			}
		};

		const handleSelectAllChange = (isEnabled) => {
			if (portfolioSettings.notificationTypes && portfolioSettings.id) {
				// Update all notification checkboxes
				portfolioSettings.notificationTypes.forEach((notificationType) => {
					setValue(`notification_${notificationType.id}`, isEnabled);
					dispatch(
						setNotification(
							portfolioSettings.id,
							notificationType.id,
							isEnabled
						)
					);
				});
			}
		};

		// Show loading state
		if (isLoading && !isInitialized) {
			return (
				<div className="property-notifications-section">
					<div className="notifications-loading">
						<Spinner />
						<span className="loading-text">
							Loading notification settings...
						</span>
					</div>
				</div>
			);
		}

		// Show error state if no user
		if (!userId) {
			return (
				<div className="property-notifications-section">
					<div className="notifications-cta">
						<div className="cta-icon">
							<i className="bi bi-bell-slash"></i>
						</div>
						<h6>Sign Up for Email Alerts</h6>
						<p>
							Create an account to receive notifications about this property.
						</p>
						<button className="btn btn-primary btn-sm">Sign Up</button>
					</div>
				</div>
			);
		}

		// Show empty state if no notification types available
		if (
			!portfolioSettings.notificationTypes ||
			portfolioSettings.notificationTypes.length === 0
		) {
			return (
				<div className="property-notifications-section">
					<div className="notifications-empty">
						<div className="empty-icon">
							<i className="bi bi-bell"></i>
						</div>
						<p>No notification settings available for this property.</p>
					</div>
				</div>
			);
		}

		return (
			<div className="property-notifications-section">
				<div className="notifications-content">
					{propertyAddress && (
						<div className="property-address-header">
							<i className="bi bi-geo-alt"></i>
							<span>{propertyAddress}</span>
						</div>
					)}

					<div className="notification-checkboxes">
						{/* Select All Checkbox */}
						<div className="select-all-item">
							<FormCheckbox
								name="selectAll"
								control={control}
								label="Enable All Notifications"
								disabled={isLoading}
								onChange={handleSelectAllChange}
								className="mb-3"
							/>
							<hr className="select-all-divider" />
						</div>

						{/* Individual Notification Checkboxes */}
						{portfolioSettings.notificationTypes.map((notificationType) => (
							<div key={notificationType.id} className="notification-item">
								<FormCheckbox
									name={`notification_${notificationType.id}`}
									control={control}
									label={notificationType.name}
									disabled={isLoading}
									onChange={(isEnabled) =>
										handleCheckboxChange(notificationType.id, isEnabled)
									}
									className="mb-2"
								/>
								{notificationType.description && (
									<p className="notification-description">
										{notificationType.description}
									</p>
								)}
							</div>
						))}
					</div>

					<div className="notifications-footer">
						<small className="text-muted">
							<i className="bi bi-info-circle me-1"></i>
							Changes are saved automatically
						</small>
					</div>
				</div>
			</div>
		);
	}
);

PropertyNotificationsSection.propTypes = {
	propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		.isRequired,
	propertyAddress: PropTypes.string,
};

PropertyNotificationsSection.defaultProps = {
	propertyAddress: '',
};

PropertyNotificationsSection.displayName = 'PropertyNotificationsSection';

export default PropertyNotificationsSection;
